import React, { useMemo } from 'react';
import { ResponseContinuum } from './ResponseContinuum';
import { FollicleField } from './FollicleField';
import { ResultSummary } from './ResultSummary';
import { ActionButton } from './ActionButton';
import { productById } from '../data/products';
import { reserveCeiling, predictOocytes } from '../utils/responseModel';
import { formatDose, formatTreatment, modelPosition } from '../utils/treatmentModel';
import type { DoseResult, PatientPreset } from '../types/simulation';
import type { Treatment } from '../types/treatment';
type ResponseStepProps = {
  patient: PatientPreset;
  treatment: Treatment;
  indiv: DoseResult;
  hasRun: boolean;
  runKey: number;
  revealed: boolean;
  reduced: boolean;
  onRun: () => void;
  onReplay: () => void;
  onReveal: () => void;
  actions: React.ReactNode;
};
export function ResponseStep({
  patient,
  treatment,
  indiv,
  hasRun,
  runKey,
  revealed,
  reduced,
  onRun,
  onReplay,
  onReveal,
  actions
}: ResponseStepProps) {
  const product = productById(treatment.product);
  const currentLabel = formatTreatment(treatment);
  const model = useMemo(() => ({
    userN: modelPosition(patient, treatment),
    indivN: predictOocytes(patient.amh, indiv.dose, patient.weight),
    ceiling: reserveCeiling(patient.amh)
  }), [patient, treatment, indiv]);
  const userN = hasRun ? model.userN : null;
  const showIndiv = hasRun && revealed;
  return <div className="flex flex-col" data-id="element-257" data-mp="1r1ikhzbk96b1">
      <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-0" data-id="element-258" data-mp="1m1e0hzbg1tg2">
        {/* Right on desktop: current choice + result. First on mobile. */}
        <div className="lg:order-2 lg:border-l lg:border-rule lg:pl-10" data-id="element-259" data-mp="1h2jj91t4kcy2">
          <p className="text-sm text-graphite" data-id="element-260" data-mp="1d17suu2ffnot">Your current choice</p>
          <h2 className="mt-1 font-serif text-4xl font-normal tracking-[-0.01em] text-ink" data-id="element-261" data-mp="3bg29dqktst9c">{product.name}</h2>
          <p className="mt-3 font-serif text-[52px] font-light leading-none tracking-[-0.03em] text-ink tabular-nums sm:text-[64px]" data-id="element-262" data-mp="37eqm3rpfck4b">
            {formatDose(treatment.dose, treatment.unit).replace(` ${treatment.unit}`, '')}
            <span className="ml-3 font-sans text-2xl tracking-normal text-graphite" data-id="element-263" data-mp="19jjzgv1r4je6">{treatment.unit} / day</span>
          </p>
          <p className="mt-3 text-sm text-graphite" data-id="element-264" data-mp="vsygz7g7mbgt">
            {product.molecule}
            {treatment.manual ? ' · dose entered manually' : ''}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-5" data-id="element-265" data-mp="1ni1lmoc0dntz">
            {hasRun ? <ActionButton variant="outline" onClick={onReplay} data-id="element-266" data-mp="1shmkkyr6hw6p">
                ↺ Replay response
              </ActionButton> : <ActionButton variant="go" onClick={onRun} data-id="element-267" data-mp="1ehbonhb0hreo">
                Run response
              </ActionButton>}
            {!hasRun && <p className="text-sm text-graphite" data-id="element-268" data-mp="31wwqrxntpqf">See where this lands for Patient {patient.id}.</p>}
          </div>

          <div className="mt-8 border-t border-rule pt-8" data-id="element-269" data-mp="81sy0jez332a">
            <ResultSummary patient={patient} userN={userN} currentLabel={currentLabel} isRekovelleUser={treatment.product === 'rekovelle'} indiv={indiv} indivN={model.indivN} revealed={revealed} onReveal={onReveal} data-id="element-270" data-mp="7z6le5d85xd7" />
          </div>
        </div>

        {/* Left on desktop: the visual response model. */}
        <div className="lg:order-1 lg:pr-10" data-id="element-271" data-mp="3cynd2n85hgbs">
          <ResponseContinuum user={userN === null ? null : {
          n: userN,
          value: currentLabel
        }} indiv={showIndiv ? {
          n: model.indivN,
          value: `${indiv.dose.toFixed(2)} μg`
        } : null} ceiling={hasRun ? model.ceiling : null} reduced={reduced} data-id="element-272" data-mp="3czos12saemys" />

      <div className="mt-8 grid grid-cols-2 gap-6 md:gap-10" data-id="element-273" data-mp="3hx6zeoqnou4o">
        <FollicleField n={userN} variant="choice" runKey={runKey} reduced={reduced} caption="Your current choice" detail={currentLabel} emptyLabel="Run response" data-id="element-274" data-mp="2u85mopasiwtl" />
        <FollicleField n={showIndiv ? model.indivN : null} variant="individualized" runKey={runKey} reduced={reduced} caption="REKOVELLE individualized" detail={showIndiv ? `${indiv.dose.toFixed(2)} μg · AMH + body weight` : 'AMH + body weight'} emptyLabel="Not revealed" data-id="element-275" data-mp="1sgcrvwzt6wds" />
      </div>

      <p className="mt-4 text-center text-xs text-graphite/80" data-id="element-276" data-mp="2b7vvo61nqegv">
        Follicle fields are an illustrative visualization of response tendency, not a prediction of follicle count.
      </p>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-end gap-4" data-id="element-277" data-mp="2l7mo826nqjeb">{actions}</div>
    </div>;
}